import React from 'react';
import { BaseResourceListProps, SimpleList, SvgBin, SvgEdit } from '@app/components';
import { catalogApi } from '@app/store';
import { ICatalog } from '@app/common';
import { theme } from '@app/theme';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { SvgTransfer } from '@app/components/icons/SvgTransfer';
import { CheckPermissions } from '@app/components/CheckPermissions';

const columnHelper = createColumnHelper<ICatalog>();

interface CatalogListProps extends BaseResourceListProps<ICatalog> {
  onPublish: (catalog: ICatalog) => void;
}

export const CatalogList = ({ onPublish, onUpdate, onDelete, onRowPress, selectedRowItem }: CatalogListProps) => {
  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <SimpleList.HeaderItem width="45%">
          <SimpleList.HeaderItemLabel>Nom</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        const catalog = info.row.original;
        const imageUrl = catalog.ticketInfos?.logo;

        return (
          <SimpleList.ItemCol width="45%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemImage source={imageUrl} alt={info.getValue()} />
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
                {info.getValue()}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('catalogToConnectors', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Connecteurs</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue().map(catalogToConnector => catalogToConnector.connector.name).join(', ')}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <SimpleList.HeaderItem width="30%" align='center'>
          <SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="30%">
          <SimpleList.ItemActions>
            {row.original.catalogToConnectors.length > 0 && (
              <CheckPermissions permissions={['dashboard::catalogs.publish']}>
                <SimpleList.ItemAction onPress={() => onPublish(row.original)}>
                  <SvgTransfer color={active ? '#FFF' : theme.colors.celadonblue} />
                </SimpleList.ItemAction>
              </CheckPermissions>
            )}

            <CheckPermissions permissions={['dashboard::catalogs.update']}>
              <SimpleList.ItemAction onPress={() => onUpdate(row.original)}>
                <SvgEdit color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>

            <CheckPermissions permissions={['dashboard::catalogs.delete']}>
              <SimpleList.ItemAction onPress={() => onDelete(row.original)}>
                <SvgBin color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>
          </SimpleList.ItemActions>
        </SimpleList.ItemCol >
      ),
    }),
  ];

  const { table, setSearch, isFetching } = useTable({
    columns,
    useQuery: ({ pagination, search }) => catalogApi.useReadManyCatalogsQuery({
      pagination,
      filters: {
        name: {
          $contains: search
        }
      },
      sort: ['name:ASC'],
      populate: [
        'catalogToConnectors',
        'catalogToConnectors.connector'
      ]
    })
  });

  return <SimpleList
    table={table}
    isFetching={isFetching}
    onSearch={setSearch}
    selectedRowItem={selectedRowItem}
    onRowPress={onRowPress} />;
};
